/**
 * Dónde vuelve alguien que ya empezó.
 *
 * Sin esta pantalla, retomar un cuestionario a medias exige haber guardado la
 * URL — y quien lo deja a medias suele ser precisamente quien cierra la pestaña
 * sin pensar en volver. El estado ya vivía en el servidor desde H4; lo que
 * faltaba era la puerta.
 */

import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { Logo } from "../components/Logo";
import { BarraSesion } from "../components/Sesion";
import { EntradaAlMapa } from "../components/EntradaAlMapa";
import api, { type SessionSummary } from "../lib/sessions";

const FECHA: Intl.DateTimeFormatOptions = { day: "numeric", month: "long", year: "numeric" };

function Estado({ sesion }: { sesion: SessionSummary }) {
  if (sesion.has_report) {
    return (
      <span className="rounded-full bg-ink/10 px-3 py-1 text-caption font-medium text-teal">
        Diagnóstico listo
      </span>
    );
  }
  if (sesion.status === "failed") {
    return (
      <span className="rounded-full bg-terracotta/10 px-3 py-1 text-caption font-medium text-terracotta">
        No se pudo calcular
      </span>
    );
  }
  return (
    <span className="rounded-full bg-paper-2 px-3 py-1 text-caption font-medium text-teal">
      {sesion.progress.percent}% completado
    </span>
  );
}

function Fila({ sesion }: { sesion: SessionSummary }) {
  const empezado = new Date(sesion.created_at).toLocaleDateString("es-ES", FECHA);
  const siguiente = sesion.progress.next_step;

  // Un cuestionario a medias se retoma donde se dejó, no desde el principio.
  const destino = sesion.has_report
    ? `/informe/${sesion.id}`
    : `/cuestionario/${sesion.id}/${siguiente ?? sesion.progress.steps[0]}`;

  return (
    <li className="critero-card flex flex-wrap items-center justify-between gap-4">
      <div className="min-w-0">
        <p className="font-medium text-ink">
          {sesion.has_report ? "Diagnóstico completo" : "Cuestionario a medias"}
        </p>
        <p className="mt-1 text-caption text-ink/60">
          Empezado el {empezado} · método {sesion.method_version}
        </p>
        {!sesion.has_report ? (
          <div className="mt-3 h-1 w-48 max-w-full overflow-hidden rounded-full bg-paper-2">
            <div
              className="h-full rounded-full bg-ink"
              style={{ width: `${sesion.progress.percent}%` }}
            />
          </div>
        ) : null}
      </div>

      <div className="flex shrink-0 items-center gap-4">
        <Estado sesion={sesion} />
        <Link
          to={destino}
          // Botones cuadrados, como pide el manual: el primario en tinta y el de
          // seguir, secundario. Se quedaron redondeados en la pasada de marca.
          className={
            (sesion.has_report ? "critero-boton" : "critero-boton-secundario") + " px-4 py-2"
          }
        >
          {sesion.has_report ? "Ver informe" : "Continuar"}
        </Link>
      </div>
    </li>
  );
}

export default function Resultados() {
  const consulta = useQuery({ queryKey: ["sesiones"], queryFn: api.listSessions });

  return (
    <div className="min-h-screen">
      <header className="mx-auto flex max-w-4xl flex-wrap items-center justify-between gap-4 px-6 py-8">
        <Link to="/" className="inline-flex">
          <Logo />
        </Link>
        <BarraSesion />
      </header>

      <main className="mx-auto max-w-4xl px-6 pb-24">
        <h1 className="text-h2">Tus diagnósticos</h1>

        <div className="mt-8">
          <EntradaAlMapa />
        </div>

        {consulta.isLoading ? <p className="mt-8 text-ink/60">Cargando…</p> : null}

        {consulta.isError ? (
          <p className="mt-8 text-ink/60">No se han podido cargar. Vuelve a intentarlo.</p>
        ) : null}

        {consulta.data?.length === 0 ? (
          <div className="mt-8">
            <p className="max-w-prose text-ink/70">
              Todavía no has empezado ninguno. El cuestionario son unas 130 preguntas y se puede
              contestar a ratos: cada respuesta se guarda sola.
            </p>
            <Link
              to="/cuestionario"
              className="mt-6 inline-block critero-boton"
            >
              Empezar el diagnóstico
            </Link>
          </div>
        ) : null}

        {consulta.data && consulta.data.length > 0 ? (
          <>
            <ul className="mt-8 space-y-4">
              {consulta.data.map((s) => (
                <Fila key={s.id} sesion={s} />
              ))}
            </ul>
            <Link
              to="/cuestionario"
              className="mt-8 inline-block text-sm text-teal underline underline-offset-4"
            >
              Empezar un diagnóstico nuevo
            </Link>
          </>
        ) : null}
      </main>
    </div>
  );
}
